import React, { useMemo, useState } from 'react';
import { ListIcon, SlidersHorizontalIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CategoryKey } from './categoryData';
type CatalogView = 'grid' | 'list';
type SortOption = 'default' | 'name-asc' | 'name-desc';
type FoodFamily = 'Tous' | 'Huile d’Olive' | 'Fruits et Légumes' | 'Fruits Frais' | 'Fruits secs' | 'Produits Laitiers' | 'Viandes et Poissons';
type FoodCatalogProps = {
  selectedSubcategory: string | null;
  onCategorySelect: (categoryKey: CategoryKey, subcategory?: string) => void;
};
type FoodProduct = {
  name: string;
  family: Exclude<FoodFamily, 'Tous'>;
  details: string;
  image: string;
};
type FoodFamilyDefinition = {
  label: Exclude<FoodFamily, 'Tous'>;
  count: number;
};
const EXPLORER_CATEGORIES: {
  key: CategoryKey;
  label: string;
  count: number;
}[] = [{
  key: 'alimentation',
  label: 'Alimentation',
  count: 47
}, {
  key: 'boisson',
  label: 'Boisson',
  count: 25
}, {
  key: 'electronique',
  label: 'Électronique',
  count: 6
}, {
  key: 'maison-decoration',
  label: 'Maison et Décoration',
  count: 16
}];
const FOOD_FAMILIES: FoodFamilyDefinition[] = [{
  label: 'Huile d’Olive',
  count: 6
}, {
  label: 'Fruits et Légumes',
  count: 19
}, {
  label: 'Fruits Frais',
  count: 13
}, {
  label: 'Fruits secs',
  count: 7
}, {
  label: 'Produits Laitiers',
  count: 7
}, {
  label: 'Viandes et Poissons',
  count: 15
}];
const PRODUCTS: FoodProduct[] = [{
  name: 'Huile d’olive vierge extra – 75 cl',
  family: 'Huile d’Olive',
  details: 'Première pression à froid',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/14757559-5add-421c-9072-fbc224d64ecf.jpg'
}, {
  name: 'Huile d’olive douce sélection – 1 L',
  family: 'Huile d’Olive',
  details: 'Format professionnel',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/14757559-5add-421c-9072-fbc224d64ecf.jpg'
}, {
  name: 'Panier de légumes de saison',
  family: 'Fruits et Légumes',
  details: 'Fraîcheur du marché',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6f09163b-5b85-4eec-8ade-ffc631ea9a59.jpg'
}, {
  name: 'Assortiment de légumes frais',
  family: 'Fruits et Légumes',
  details: 'Tomates, courgettes et primeurs',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6f09163b-5b85-4eec-8ade-ffc631ea9a59.jpg'
}, {
  name: 'Fruits frais sélection du jour',
  family: 'Fruits Frais',
  details: 'Variété et qualité',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/34d4cf5e-590f-4412-927d-2c9975a4270d.jpg'
}, {
  name: 'Corbeille de fruits de saison',
  family: 'Fruits Frais',
  details: 'Prête pour vos rayons',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/34d4cf5e-590f-4412-927d-2c9975a4270d.jpg'
}, {
  name: 'Mélange de fruits secs premium',
  family: 'Fruits secs',
  details: 'Amandes, noix et raisins',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6feeb19c-08d0-4c7f-b5e0-3209721de658.jpg'
}, {
  name: 'Assortiment amandes et noisettes',
  family: 'Fruits secs',
  details: 'Sélection gourmande',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/6feeb19c-08d0-4c7f-b5e0-3209721de658.jpg'
}, {
  name: 'Assortiment laitier pour professionnels',
  family: 'Produits Laitiers',
  details: 'Lait, beurre et crèmerie',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb2d7d77-2279-4f86-b082-7e3b2c479e06.jpg'
}, {
  name: 'Sélection de fromages et yaourts',
  family: 'Produits Laitiers',
  details: 'Fraîcheur garantie',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb2d7d77-2279-4f86-b082-7e3b2c479e06.jpg'
}, {
  name: 'Crevettes cuites Bio de Madagascar – 250 g',
  family: 'Viandes et Poissons',
  details: 'Poissons et fruits de mer',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ffe18bdb-11f1-4813-b7f5-4c05e9cb616c.jpg'
}, {
  name: 'Cuisses de poulet blanc Label Rouge – 3 kg',
  family: 'Viandes et Poissons',
  details: 'Volaille sélectionnée',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/bbd63276-567b-4bf8-8bdf-a6e33ada0b31.jpg'
}];
export function FoodCatalog({
  selectedSubcategory,
  onCategorySelect
}: FoodCatalogProps) {
  const [sort, setSort] = useState<SortOption>('default');
  const [view, setView] = useState<CatalogView>('grid');
  const family = isFoodFamily(selectedSubcategory) ? selectedSubcategory : 'Tous';
  const products = useMemo(() => {
    const filteredProducts = family === 'Tous' ? PRODUCTS : PRODUCTS.filter(product => product.family === family);
    if (sort === 'default') return filteredProducts;
    return [...filteredProducts].sort((first, second) => {
      const result = first.name.localeCompare(second.name, 'fr');
      return sort === 'name-asc' ? result : -result;
    });
  }, [family, sort]);
  const selectFamily = (nextFamily: FoodFamily) => {
    onCategorySelect('alimentation', nextFamily === 'Tous' ? undefined : nextFamily);
  };
  return <section className="mt-8 overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(10,51,32,0.12)]" aria-labelledby="food-catalog-title" data-id="element-163" data-mp="1j1xvkstzp5r3">
      <div className="relative min-h-[250px] overflow-hidden bg-[#f4fae8] px-6 py-12 sm:min-h-[280px] sm:px-10 lg:px-14 lg:py-16" data-id="element-164" data-mp="1e5pl3sqqm0q9">
        <img src="https://cdn.magicpatterns.com/patterns/generated-images/34d4cf5e-590f-4412-927d-2c9975a4270d.jpg" alt="" className="pointer-events-none absolute -right-8 bottom-0 hidden h-[115%] w-[38%] object-contain opacity-90 lg:block" data-id="element-165" data-mp="1n4kpcza810qx" />
        <div className="relative max-w-2xl" data-id="element-166" data-mp="1x1z6l13yxtka">
          <p className="text-sm text-gray-500" data-id="element-167" data-mp="2atvsgnf1n788">Accueil <span aria-hidden="true" data-id="element-168" data-mp="2asltzx4a3x12">›</span> <span className="font-medium text-ardis-green" data-id="element-169" data-mp="2avzueztdtw5v">Alimentation</span></p>
          <h3 id="food-catalog-title" className="mt-4 font-display text-4xl font-bold tracking-tight text-ardis-green sm:text-5xl" data-id="element-170" data-mp="26stt86l4mv69">Alimentation</h3>
          <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base" data-id="element-171" data-mp="2ft8hnete0qdz">Une sélection professionnelle d’huiles d’olive, de fruits, de produits frais, de produits laitiers, de viandes et de poissons.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[250px_minmax(0,1fr)]" data-id="element-172" data-mp="1hhyv5gcoeyjf">
        <aside className="border-b border-gray-100 bg-gray-50/70 p-6 lg:border-b-0 lg:border-r lg:p-8" aria-label="Explorer les catégories alimentaires" data-id="element-173" data-mp="1mgifjcbmg29o">
          <h4 className="font-display text-xl font-bold text-ardis-green" data-id="element-174" data-mp="1r8a1f9ju1jth">Explorer</h4>
          <ul className="mt-5 space-y-1 border-t border-gray-200 pt-4" data-id="element-175" data-mp="1w5jx36xterpr">
            {EXPLORER_CATEGORIES.map(category => {
            const isCurrent = category.key === 'alimentation';
            return <li key={category.key} data-id="element-176" data-mp="2581hrgxtncan">
                  <button type="button" onClick={() => onCategorySelect(category.key)} aria-current={isCurrent && family === 'Tous' ? 'page' : undefined} className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${isCurrent ? 'text-ardis-green' : 'text-gray-700 hover:bg-white hover:text-ardis-green'}`} data-id="element-177" data-mp="2a5upieerwed1">
                    <span className="flex items-center gap-2" data-id="element-178" data-mp="3gj8wpgo6m35"><span className="text-ardis-yellow" aria-hidden="true" data-id="element-179" data-mp="3fhmxpy48f6n">›</span>{category.label}</span>
                    <span className={isCurrent ? 'text-ardis-yellow' : 'text-gray-400'} data-id="element-180" data-mp="3ulizk2xqbgue">({category.count})</span>
                  </button>
                </li>;
          })}
          </ul>

          <ul className="mt-4 space-y-1 border-t border-gray-200 pt-4" aria-label="Familles alimentaires" data-id="element-181" data-mp="r52id8bgh4wt">
            {FOOD_FAMILIES.map(item => {
            const isActive = family === item.label;
            return <li key={item.label} data-id="element-182" data-mp="hb21yaaq4d8s">
                  <button type="button" onClick={() => selectFamily(item.label)} aria-current={isActive ? 'page' : undefined} className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${isActive ? 'bg-white text-ardis-green' : 'text-gray-700 hover:bg-white hover:text-ardis-green'}`} data-id="element-183" data-mp="d7x78twvxfup">
                    <span className="flex items-center gap-2" data-id="element-184" data-mp="15wuujz2jnp28"><span className="text-ardis-yellow" aria-hidden="true" data-id="element-185" data-mp="15tp8ksp7p3aq">›</span>{item.label}</span>
                    <span className={isActive ? 'text-ardis-yellow' : 'text-gray-400'} data-id="element-186" data-mp="ryn0q00t13va">({item.count})</span>
                  </button>
                </li>;
          })}
          </ul>
        </aside>

        <div className="min-w-0 p-6 sm:p-8" data-id="element-187" data-mp="1knbxjmutun18">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-5" data-id="element-188" data-mp="1olhyufcwcaer">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-189" data-mp="1avoady5azs5k">
              <p className="text-sm font-medium text-gray-700" data-id="element-190" data-mp="1ftpt2hythpgi">Affichage de <span className="font-semibold text-ardis-green" data-id="element-191" data-mp="1fs4ywtgf08j6">1–{products.length}</span> sur 47 résultats</p>
              <div className="flex flex-wrap items-center gap-2" aria-label="Options d’affichage et de tri" data-id="element-192" data-mp="23hz8n25ftgz7">
                <div className="flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Mode d’affichage" data-id="element-193" data-mp="2hpcazxbkvec8">
                  <button type="button" aria-label="Afficher en grille" aria-pressed={view === 'grid'} onClick={() => setView('grid')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'grid' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-194" data-mp="2dojzsyj5azdb">
                    <div className="h-4 w-4" aria-hidden="true" data-id="element-195" data-mp="1yrj8gw4v96fd" />
                  </button>
                  <button type="button" aria-label="Afficher en liste" aria-pressed={view === 'list'} onClick={() => setView('list')} className={`rounded p-2 focus:outline-none focus:ring-2 focus:ring-ardis-blue ${view === 'list' ? 'bg-ardis-green text-white' : 'text-gray-500 hover:text-ardis-green'}`} data-id="element-196" data-mp="1upo5zob78n00">
                    <ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-197" data-mp="3k2rswecc1c71" />
                  </button>
                </div>
                <label className="relative flex items-center" data-id="element-198" data-mp="3f481vzivta7t">
                  <SlidersHorizontalIcon className="pointer-events-none absolute left-3 h-4 w-4 text-ardis-green" aria-hidden="true" data-id="element-199" data-mp="2oh61ujgmjv7q" />
                  <span className="sr-only" data-id="element-200" data-mp="2jibkllyb2epq">Trier les produits</span>
                  <select value={sort} onChange={event => setSort(event.target.value as SortOption)} className="h-10 appearance-none rounded-md border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm font-medium text-gray-700 outline-none focus:border-ardis-blue focus:ring-2 focus:ring-ardis-blue/20" data-id="element-201" data-mp="2eqbe1anqcnk6">
                    <option value="default" data-id="element-202" data-mp="20qaebyj7bm6a">Tri par défaut</option>
                    <option value="name-asc" data-id="element-203" data-mp="1vs8sx6yazarn">Nom, A à Z</option>
                    <option value="name-desc" data-id="element-204" data-mp="1vshavd9a969p">Nom, Z à A</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par famille alimentaire" data-id="element-205" data-mp="3q6rwzdyhtmbs">
              {(['Tous', ...FOOD_FAMILIES.map(item => item.label)] as FoodFamily[]).map(item => <button key={item} type="button" aria-pressed={family === item} onClick={() => selectFamily(item)} className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ardis-blue focus:ring-offset-2 ${family === item ? 'border-ardis-green bg-ardis-green text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-ardis-green hover:text-ardis-green'}`} data-id="element-206" data-mp="3tofr5f6zsunh">
                  {item}
                </button>)}
            </div>
          </div>

          <motion.div layout className={view === 'grid' ? 'mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 xl:grid-cols-3' : 'mt-6 space-y-4'} data-id="element-207" data-mp="3eq56t20pwjpr">
            {products.map((product, index) => {
            const quoteUrl = `mailto:contact@lepiandistribution.com?subject=${encodeURIComponent(`Demande de devis — ${product.name}`)}`;
            return <motion.article layout key={product.name} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.28,
              delay: index * 0.04
            }} className={view === 'grid' ? 'flex flex-col' : 'flex flex-col gap-5 rounded-xl border border-gray-100 p-4 sm:flex-row sm:items-center'} data-id="element-208" data-mp="yz99csif992i" data-mpi={index}>
                  <div className={view === 'grid' ? 'aspect-square overflow-hidden bg-white' : 'h-36 w-full flex-shrink-0 overflow-hidden bg-white sm:w-44'} data-id="element-209" data-mp="j9taxlkcv93x">
                    <img src={product.image} alt={product.name} className="h-full w-full object-contain" data-id="element-210" data-mp="3rq4jsly8aenm" />
                  </div>
                  <div className={view === 'grid' ? 'flex flex-1 flex-col pt-4' : 'flex flex-1 flex-col'} data-id="element-211" data-mp="3mooxkqkbe23n">
                    <p className="text-xs font-medium text-ardis-green/70" data-id="element-212" data-mp="jbkxoqwqrm9">{product.family} · {product.details}</p>
                    <h5 className="mt-2 font-display text-sm font-semibold leading-relaxed text-gray-900" data-id="element-213" data-mp="38yvb2ewa7tx3">{product.name}</h5>
                    <a href={quoteUrl} className="mt-4 inline-flex w-fit items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2" data-id="element-214" data-mp="33yz6adfrfygh">Demandez un devis</a>
                  </div>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}
function isFoodFamily(value: string | null): value is Exclude<FoodFamily, 'Tous'> {
  return FOOD_FAMILIES.some(item => item.label === value);
}